import { useCallback, useEffect, useReducer, useRef } from 'react';
import { queryClient } from '@/lib/queryClient';
import { useAgentTurnStream, type AgentAttachment } from '@/hooks/useAgentTurnStream';
import { retryRequest, turnReducer, type LiveTurn, type TurnRequest } from './turnState';

export interface AgentThreadController {
  turn: LiveTurn | null;
  isStreaming: boolean;
  /** `projectContext: false` when the advisor chose Global in a project thread. */
  send: (sessionId: number, content: string, options?: { projectContext?: boolean; attachments?: AgentAttachment[] }) => Promise<void>;
  regenerate: (sessionId: number) => Promise<void>;
  edit: (sessionId: number, messageId: number, content: string) => Promise<void>;
  retry: () => Promise<void>;
  stop: () => void;
  dismissError: () => void;
}

const sessionKey = (sessionId: number) => ['/api/smart-chat/sessions', sessionId];

/**
 * Runs Threads turns on AgentCore. The server persists messages; this keeps the live turn
 * (tokens, steps, errors) and refetches the thread when a turn ends so the saved reply takes over.
 * Only the advisor's own text and the action go to the server, never UI state.
 */
export function useAgentThread(options: { onBusyChange?: (busy: boolean, sessionId: number) => void } = {}): AgentThreadController {
  const stream = useAgentTurnStream();
  const [turn, dispatch] = useReducer(turnReducer, null);
  const turnRef = useRef(turn);
  turnRef.current = turn;
  const onBusyChange = useRef(options.onBusyChange);
  useEffect(() => {
    onBusyChange.current = options.onBusyChange;
  }, [options.onBusyChange]);

  const run = useCallback(
    async (request: TurnRequest) => {
      const { sessionId } = request;
      dispatch({ type: 'start', request });
      onBusyChange.current?.(true, sessionId);

      const key = sessionKey(sessionId);
      await queryClient.cancelQueries({ queryKey: key });
      if (request.action !== 'regenerate') {
        queryClient.setQueryData(key, (old: any) => {
          const base = old && !Array.isArray(old) ? old : { session: { id: sessionId, title: 'New Conversation' }, messages: [] };
          const optimistic = {
            id: -Date.now(),
            sessionId,
            role: 'user',
            content: request.content,
            createdAt: new Date().toISOString(),
            optimistic: true,
            metadata: { engine: 'agentcore', ...(request.attachments?.length ? { attachments: request.attachments.map((a) => ({ name: a.name })) } : {}) },
          };
          return { ...base, messages: [...(base.messages || []), optimistic], __pendingRequest: true };
        });
      }

      await stream.start(
        sessionId,
        {
          content: request.content,
          action: request.action,
          targetMessageId: request.targetMessageId,
          ...(request.projectContext === false ? { projectContext: false } : {}),
          ...(request.attachments?.length ? { attachments: request.attachments } : {}),
        },
        {
          onMeta: (d) => dispatch({ type: 'meta', userMessageId: d.userMessageId, turnId: d.turnId }),
          onStatus: (d) => dispatch({ type: 'status', ...d }),
          onTool: (d) => dispatch({ type: 'tool', ...d }),
          onToken: (delta) => dispatch({ type: 'token', delta }),
          onReplace: (content) => dispatch({ type: 'replace', content }),
          onDone: (data) => dispatch({ type: 'done', data }),
          onError: (data) => dispatch({ type: 'error', data }),
        },
      );

      try {
        await queryClient.refetchQueries({ queryKey: key, exact: true });
        // After Stop (or a dropped connection) the server may still be winding the turn down for
        // a moment. Re-check briefly so the thread doesn't stay stuck on "working on this".
        for (let i = 0; i < 10 && (queryClient.getQueryData(key) as any)?.session?.pendingResponse; i++) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          await queryClient.refetchQueries({ queryKey: key, exact: true });
        }
      } finally {
        dispatch({ type: 'settled' });
        onBusyChange.current?.(false, sessionId);
      }
    },
    [stream.start],
  );

  const send = useCallback(
    (sessionId: number, content: string, options?: { projectContext?: boolean; attachments?: AgentAttachment[] }) =>
      run({
        sessionId,
        action: 'send',
        content,
        ...(options?.projectContext === false ? { projectContext: false } : {}),
        ...(options?.attachments?.length ? { attachments: options.attachments } : {}),
      }),
    [run],
  );
  const regenerate = useCallback((sessionId: number) => run({ sessionId, action: 'regenerate', content: '' }), [run]);
  const edit = useCallback(
    (sessionId: number, messageId: number, content: string) => run({ sessionId, action: 'edit', content, targetMessageId: messageId }),
    [run],
  );
  const retry = useCallback(async () => {
    const current = turnRef.current;
    if (current) await run(retryRequest(current));
  }, [run]);
  const dismissError = useCallback(() => dispatch({ type: 'dismiss' }), []);
  const stop = useCallback(() => {
    const current = turnRef.current;
    void stream.stop(
      current?.turnId ? { sessionId: current.request.sessionId, turnId: current.turnId, shownChars: current.content.length } : undefined,
    );
  }, [stream.stop]);

  return { turn, isStreaming: stream.isStreaming, send, regenerate, edit, retry, stop, dismissError };
}

/**
 * Keeps the open thread in the URL (`/threads?sessionId=42`) so threads can be linked and
 * survive a refresh. Only touches the URL while on /threads.
 */
/**
 * Starts the advisor's AgentCore runtime while Threads is open, so the first message doesn't wait
 * for a cold start. Fire-and-forget; the server dedupes. Re-sent when the advisor returns to the tab.
 */
export function useAgentWarmup(enabled: boolean) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const warm = () => {
      if (document.visibilityState !== 'visible') return;
      fetch('/api/threads-agent/warm', { method: 'POST', credentials: 'include' }).catch(() => undefined);
    };
    warm();
    document.addEventListener('visibilitychange', warm);
    return () => document.removeEventListener('visibilitychange', warm);
  }, [enabled]);
}

export function useThreadUrlSync(sessionId: number | null, enabled: boolean) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || window.location.pathname !== '/threads') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('sessionId') === (sessionId ? String(sessionId) : null)) return;
    if (sessionId) params.set('sessionId', String(sessionId));
    else params.delete('sessionId');
    const search = params.toString();
    window.history.replaceState(window.history.state, '', `/threads${search ? `?${search}` : ''}`);
  }, [sessionId, enabled]);
}
